import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './CoachFeedback.module.css';

// Pre-defined feedback messages to avoid re-creation
const FEEDBACK_MESSAGES = {
    facial: {
        high: { text: "You're expressive! Keep it up! 🎉", type: 'success', mood: 'excited' },
        mid: { text: "Try adding more expressions! 🤔", type: 'warning', mood: 'neutral' },
        low: { text: "Too neutral! Show more emotions! 😐", type: 'error', mood: 'concerned' }
    },
    hands: {
        high: { text: "Great gestures! 🙌", type: 'success', mood: 'excited' },
        low: { text: "Your hands aren't visible! 🚨", type: 'error', mood: 'concerned' }
    },
    posture: {
        high: { text: "Excellent posture! 🎯", type: 'success', mood: 'proud' },
        low: { text: "You're slouching! Fix your posture! 🔴", type: 'error', mood: 'concerned' }
    }
};

// Configuration constants
const LOW_EXPRESSION_THRESHOLD = 40;
const HIGH_EXPRESSION_THRESHOLD = 80;
const LOW_EXPRESSION_DELAY = 3000;
const FEEDBACK_DISPLAY_TIME = 4000;
const FEEDBACK_INTERVAL = 15000;

const CoachFeedback = ({
    facialEngagement,
    handsVisible,
    goodPosture,
    isRecording,
    activeIntro,
    onFeedbackChange
}) => {
    const [feedback, setFeedback] = useState(null);
    const [visible, setVisible] = useState(false);

    // Use refs for values that don't affect rendering
    const feedbackTimerRef = useRef(null);
    const feedbackIntervalRef = useRef(null);
    const lastFeedbackRef = useRef('');
    const lowExpressionStartRef = useRef(null);
    const expressionStateRef = useRef('normal');

    // Determine feedback using a memoized function
    const determineFeedback = useCallback(() => {
        const now = Date.now();

        // Only show low expression feedback if it's been low for at least 3 seconds
        const shouldShowLowExpressionFeedback =
            lowExpressionStartRef.current &&
            (now - lowExpressionStartRef.current > LOW_EXPRESSION_DELAY);

        // Choose metric with biggest problem first
        if (shouldShowLowExpressionFeedback) {
            return { ...FEEDBACK_MESSAGES.facial.low, metric: 'facial' };
        }
        if (!handsVisible) {
            return { ...FEEDBACK_MESSAGES.hands.low, metric: 'hands' };
        }
        if (!goodPosture) {
            return { ...FEEDBACK_MESSAGES.posture.low, metric: 'posture' };
        }

        // If all metrics are OK, give positive feedback on the strongest one
        if (expressionStateRef.current === 'high') {
            return { ...FEEDBACK_MESSAGES.facial.high, metric: 'facial' };
        }
        if (handsVisible) {
            return { ...FEEDBACK_MESSAGES.hands.high, metric: 'hands' };
        }
        if (goodPosture) {
            return { ...FEEDBACK_MESSAGES.posture.high, metric: 'posture' };
        }

        // Default to facial mid-range feedback
        return { ...FEEDBACK_MESSAGES.facial.mid, metric: 'facial' };
    }, [handsVisible, goodPosture]);

    // Function to show feedback
    const showFeedback = useCallback(() => {
        const newFeedback = determineFeedback();

        // Don't show the same feedback message twice in a row
        if (newFeedback.text !== lastFeedbackRef.current) {
            setFeedback(newFeedback);
            lastFeedbackRef.current = newFeedback.text;

            // Show feedback bubble
            setVisible(true);

            // Notify parent component about the mood change
            if (onFeedbackChange) {
                onFeedbackChange(newFeedback.mood);
            }

            // Auto-hide feedback after set time
            clearTimeout(feedbackTimerRef.current);
            feedbackTimerRef.current = setTimeout(() => {
                setVisible(false);
            }, FEEDBACK_DISPLAY_TIME);
        }
    }, [determineFeedback, onFeedbackChange]);

    // Update expression state
    useEffect(() => {
        // Skip when not recording or during intro
        if (!isRecording || activeIntro) return;

        const now = Date.now();

        // Update expression state
        if (facialEngagement < LOW_EXPRESSION_THRESHOLD) {
            // Start tracking low expression if we haven't already
            if (expressionStateRef.current !== 'low') {
                lowExpressionStartRef.current = now;
                expressionStateRef.current = 'low';
            }
        } else {
            // Reset low expression tracking if expressions are adequate
            lowExpressionStartRef.current = null;
            expressionStateRef.current = facialEngagement >= HIGH_EXPRESSION_THRESHOLD ? 'high' : 'normal';
        }

        // Show feedback based on the updated state
        showFeedback();
    }, [facialEngagement, isRecording, activeIntro, showFeedback]);

    // Set up interval for periodic feedback
    useEffect(() => {
        // Reset everything when recording stops or during intro
        if (!isRecording || activeIntro) {
            setVisible(false);
            clearTimeout(feedbackTimerRef.current);
            clearInterval(feedbackIntervalRef.current);
            lowExpressionStartRef.current = null;
            expressionStateRef.current = 'normal';
            return;
        }

        // Set up interval to show feedback periodically
        feedbackIntervalRef.current = setInterval(showFeedback, FEEDBACK_INTERVAL);

        // Cleanup function
        return () => {
            clearInterval(feedbackIntervalRef.current);
            clearTimeout(feedbackTimerRef.current);
        };
    }, [isRecording, activeIntro, showFeedback]);

    // Early return if no feedback
    if (!feedback) return null;

    return (
        <div className={`${styles.feedbackContainer} ${visible ? styles.visible : ''} ${styles[feedback.type]}`}>
            <div className={styles.feedbackBubble}>
                <div className={styles.feedbackText}>{feedback.text}</div>
                <div className={styles.bubbleTail}></div>
            </div>
        </div>
    );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(CoachFeedback);